import React from "react";
import { t } from "i18next";
import { AlertTriangle } from "lucide-react";
import PrimaryButton from "./buttons/primaryButton.tsx";
import { CTA_FULL_WIDTH } from "./buttons/ctaClassNames";
import PaymentStatusMessage from "./confirmation/PaymentStatusMessage";
import { useGuestCheckoutSettings } from "./context/GuestCheckoutSettingsContext";
import { useModalOnHide } from "./ui/Modal";
import { retryWithinEmbeddedElement } from "../utils/mfeElement";
import { CREDIT_CARDS_DISABLED_ERROR } from "../utils/paymentHelpers";

interface PaymentErrorPageProps {
  paymentError: string;
}

const VALID_ROUTES = ["secure-your-booking", "manage-your-order", "place-your-order"];

interface ErrorCopy {
  titleKey: string;
  titleDefault: string;
  messageKey: string;
  messageDefault: string;
}

const GENERIC_ERROR_COPY: ErrorCopy = {
  titleKey: "payment_error_title",
  titleDefault: "Payment Issue",
  messageKey: "payment_error_generic_message",
  messageDefault:
    "Something went wrong while processing your payment. No money has been taken from your account. Please try again.",
};

/**
 * Copy for the Collins refusal messages we can explain to the guest. Anything not
 * listed falls back to the generic payment error. A Map is used so an arbitrary
 * error message can never resolve to an inherited object property.
 */
const ERROR_COPY = new Map<string, ErrorCopy>([
  [
    "The given card brand is not supported",
    {
      titleKey: "unsupported_card_brand_title",
      titleDefault: "Card Not Supported",
      messageKey: "unsupported_card_brand_message",
      messageDefault:
        "American Express (AMEX) cards are not supported. Please use a Visa or Mastercard to complete your payment.",
    },
  ],
  [
    CREDIT_CARDS_DISABLED_ERROR,
    {
      titleKey: "credit_card_disabled_title",
      titleDefault: "Credit Card Not Accepted",
      messageKey: "credit_card_disabled_message",
      messageDefault: CREDIT_CARDS_DISABLED_ERROR,
    },
  ],
]);

const PaymentErrorPage: React.FC<PaymentErrorPageProps> = ({ paymentError }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const rawAction = urlParams.get("action") ?? "secure-your-booking";
  const retryRoute = VALID_ROUTES.includes(rawAction) ? rawAction : "book";
  const copy = ERROR_COPY.get(paymentError) ?? GENERIC_ERROR_COPY;
  const { isWidget } = useGuestCheckoutSettings();
  const onModalHide = useModalOnHide();

  const errorTitle = t(copy.titleKey, copy.titleDefault);
  const errorMessage = t(copy.messageKey, copy.messageDefault);

  /**
   * The widget's confirmation modal sits on the venue's own page, where the
   * retry route does not exist — retrying must stay inside the widget. Dismiss
   * the modal so the mounted /book journey behind it resumes; without a modal
   * ancestor, reload the host page stripped of Stripe's redirect params.
   */
  const retryWithinWidget = () => {
    if (onModalHide) {
      onModalHide();
    } else {
      retryWithinEmbeddedElement(retryRoute, urlParams);
    }
  };

  if (isWidget) {
    return (
      <PaymentStatusMessage
        title={errorTitle}
        message={errorMessage}
        tone="error"
        box="error"
        testId="payment-error-page"
        action={
          <PrimaryButton
            onClick={retryWithinWidget}
            className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg ${CTA_FULL_WIDTH}`}
            text={t("try_again", "Try Again")}
            type="button"
          />
        }
      />
    );
  }

  return (
    <div data-testid="payment-error-page" role="alert" aria-live="assertive">
      <div className="gcw:min-h-screen gcw:bg-gray-50 gcw:flex gcw:items-center gcw:justify-center gcw:p-4">
        <div className="gcw:bg-white gcw:rounded-none gcw:sm:rounded-lg gcw:shadow-lg gcw:max-w-md gcw:w-full gcw:p-6 gcw:sm:p-8 gcw:text-center gcw:animate-in fade-in gcw:duration-300">
          <div className="gcw:flex gcw:justify-center gcw:mb-6 gcw:animate-in gcw:zoom-in-95 gcw:duration-700 gcw:delay-100">
            <div className="gcw:w-16 gcw:h-16 gcw:sm:w-20 gcw:sm:h-20 gcw:rounded-full gcw:bg-red-100 gcw:flex gcw:items-center gcw:justify-center">
              <AlertTriangle className="gcw:w-8 gcw:h-8 gcw:sm:w-10 gcw:sm:h-10 gcw:text-red-600" aria-hidden="true" />
            </div>
          </div>

          <div className="gcw:font-mulish">
            <h1 className="gcw:text-[32px] gcw:font-bold gcw:text-gray-900 gcw:mb-4">{errorTitle}</h1>
            <p className="gcw:text-[16px] gcw:text-gray-600 gcw:mb-6">{errorMessage}</p>
          </div>

          <PrimaryButton
            onClick={() => {
              window.location.href = `/${retryRoute}?${urlParams.toString()}`;
            }}
            className="gcw:w-full gcw:h-[56px] gcw:text-[18px] gcw:font-semibold gcw:bg-primary gcw:hover:bg-primary-hover gcw:text-white gcw:rounded-full gcw:transition-colors"
            text={t("try_again", "Try Again")}
            type="button"
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentErrorPage;
